import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Search, UserPlus } from 'lucide-react';
import { Input } from './ui';
import { MiniButton, PersonMeta } from './programAdminStyles';
import NewPersonForm from '../personAdmin/NewPersonForm';

// Picking a person for a spot (replace the person, fill an open spot). It searches through the admin
// person API (person.PersonAdminController GET /api/cms/people/search), which is the same box the
// other admin screens use, so a person who is not at this event yet can be found too. `search(q)`
// answers [{id, programName, institution, contactEmail}]. Controlled: `value` is the picked person
// id (or null) and every pick goes back out through `onPick`.
//
// With `create` and `findMatches` (person.PersonAdminController create and matches) it also offers
// "Create a new person", always reachable under the results: the People screen's form
// (personAdmin/NewPersonForm) opens in place of the search, with its duplicate check. "Use this
// person" on a candidate, or the person just created, is picked exactly like a search hit, so the
// dialog's flow goes on with them.
const PersonPicker = ({ value, search, onPick, placeholder, label, create, findMatches }) => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);
  const [creating, setCreating] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!search) return undefined;
    const mine = ++seq.current;
    setBusy(true);
    setError(null);
    const timer = setTimeout(() => {
      search(query)
        .then((rows) => { if (seq.current === mine) setHits(rows || []); })
        .catch((err) => {
          if (seq.current !== mine) return;
          setHits([]);
          setError(typeof err?.response?.data === 'string' && err.response.data
            ? err.response.data
            : intl.formatMessage({ id: 'programAdmin.picker.failed', defaultMessage: 'Could not search for people.' }));
        })
        .finally(() => { if (seq.current === mine) setBusy(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, search, intl]);

  const pick = (hit) => {
    setPicked(hit);
    setCreating(false);
    onPick?.(hit);
  };

  if (creating) {
    return (
      <div style={{ fontWeight: 400 }}>
        <p style={{ margin: '0 0 .5rem', fontSize: '.82rem', fontWeight: 700, color: '#111D4E' }}>
          <FormattedMessage id="programAdmin.picker.create" defaultMessage="Create a new person" />
        </p>
        <NewPersonForm
          findMatches={findMatches}
          create={create}
          initialName={query}
          existingLabel={intl.formatMessage({ id: 'programAdmin.picker.use', defaultMessage: 'Use this person' })}
          onExisting={(c) => pick({ id: c.personId, programName: c.programName, institution: c.institution, contactEmail: c.contactEmail })}
          onCreated={(detail) => pick({
            id: detail.id,
            programName: detail.programName,
            institution: detail.fields?.institution?.value ?? null,
            contactEmail: detail.fields?.contactEmail?.value ?? null,
          })}
          onCancel={() => setCreating(false)}
        />
      </div>
    );
  }

  return (
    <div>
      {picked && (
        <p style={{ margin: '0 0 .4rem', fontSize: '.82rem', fontWeight: 700, color: '#111D4E' }}>
          <FormattedMessage id="programAdmin.picker.picked" defaultMessage="Selected: {name}" values={{ name: picked.programName }} />
        </p>
      )}
      <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
        <Search size={14} aria-hidden="true" />
        <Input
          type="search"
          aria-label={label}
          value={query}
          placeholder={placeholder || intl.formatMessage({ id: 'programAdmin.picker.placeholder', defaultMessage: 'Search by name, email or institution' })}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: '1 1 auto' }}
        />
      </div>
      {busy && <PersonMeta><FormattedMessage id="programAdmin.picker.searching" defaultMessage="Searching…" /></PersonMeta>}
      {error && <p style={{ margin: '.4rem 0 0', fontSize: '.78rem', color: '#991b1b' }}>{error}</p>}
      {hits.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '.4rem 0 0', padding: 0, maxHeight: '10rem', overflowY: 'auto', border: '1px solid #e2e8f0' }}>
          {hits.map((hit) => (
            <li key={hit.id} style={{ borderBottom: '1px solid #eef2f7' }}>
              <MiniButton
                type="button"
                $active={value === hit.id}
                onClick={() => pick(hit)}
                style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem', width: '100%', border: 0 }}
              >
                <strong>{hit.programName}</strong>
                {hit.institution && <span style={{ color: '#64748b', fontWeight: 400 }}>{hit.institution}</span>}
                {hit.contactEmail && <span style={{ color: '#64748b', fontWeight: 400 }}>{hit.contactEmail}</span>}
              </MiniButton>
            </li>
          ))}
        </ul>
      )}
      {!busy && !error && hits.length === 0 && query && (
        <PersonMeta><FormattedMessage id="programAdmin.picker.none" defaultMessage="Nobody found for that search." /></PersonMeta>
      )}
      {create && findMatches && (
        <MiniButton type="button" onClick={() => setCreating(true)} style={{ marginTop: '.4rem' }}>
          <UserPlus size={12} aria-hidden="true" />
          <FormattedMessage id="programAdmin.picker.create" defaultMessage="Create a new person" />
        </MiniButton>
      )}
    </div>
  );
};

export default PersonPicker;
